import React, { Fragment, memo, useCallback, useState, useEffect } from 'react'
import SettingsPanel from '../../components/settings/SettingsPanel'
import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import { makeStyles } from '@material-ui/core'
import {
  getIncomingEmail,
  createIncomingEmail,
  getIncomingEmailSuffix
} from '../../data/incomingEmail'
import { hasSubscription } from '../../components/EnforceLimit'
import InputAdornment from '@material-ui/core/InputAdornment'
import InputLabel from '@material-ui/core/InputLabel'
import IconButton from '@material-ui/core/IconButton'
import Input from '@material-ui/core/Input'
import MuiAlert from '@material-ui/lab/Alert'
import FormControl from '@material-ui/core/FormControl'
import Snackbar from '@material-ui/core/Snackbar'
import Typography from '@material-ui/core/Typography'
import Link from '@material-ui/core/Link'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import LOG from '../../logging/Logger'
import Tooltip from '@material-ui/core/Tooltip'

function Alert(props) {
  return <MuiAlert elevation={6} variant='filled' {...props} />
}
const useStyles = makeStyles(theme => ({
  textField: {
    marginLeft: theme.spacing(),
    marginRight: theme.spacing(),
    width: '100%',
    fontSize: 14
  }
}))

const CREATE_INBOX_MESSAGE = 'Click "CREATE NEW INBOX" to enable automation'

const SettingsAccountAutomation = memo(({ setShowSubscriptionPlans }) => {
  LOG.debug('Render: SettingsAccountAutomation')

  const classes = useStyles()
  const emailSuffix = getIncomingEmailSuffix()
  const [emailIncoming, setEmailIncoming] = useState([])
  const [isAutomationEnabled, setIsAutomationEnabled] = useState(false)

  const [openAlert, setAlertOpen] = useState(false)

  const [openWarning, setOpenWarning] = useState(false)

  const getIncEmail = useCallback(async () => {
    const currentIncomingEmail = await getIncomingEmail()
    setEmailIncoming(currentIncomingEmail)
  }, [])

  const createIncEmail = useCallback(async () => {
    setOpenWarning(false)
    const newIncEmail = await createIncomingEmail()
    if (newIncEmail) {
      setEmailIncoming(newIncEmail)
    }
  }, [])

  const copyEmail = useCallback(() => {
    navigator.clipboard.writeText(emailIncoming + emailSuffix)
    setAlertOpen(true)
  }, [emailIncoming, emailSuffix])

  useEffect(() => {
    getIncEmail()
  }, [getIncEmail])

  const getSubscription = useCallback(async () => {
    const hasCurrentSubscription = await hasSubscription()
    setIsAutomationEnabled(hasCurrentSubscription)
  }, [])

  useEffect(() => {
    getSubscription()
  }, [getSubscription])

  const handleAlertClose = useCallback(async (event, reason) => {
    if (reason === 'clickaway') {
      return
    }

    setAlertOpen(false)
  }, [])

  const handleWarningOpen = useCallback(() => {
    setOpenWarning(true)
  }, [])

  const handleWarningClose = useCallback(() => {
    setOpenWarning(false)
  }, [])

  const handleSubscriptionOpen = useCallback(() => {
    setShowSubscriptionPlans(true)
  }, [setShowSubscriptionPlans])

  return (
    <SettingsPanel title='Automation'>
      <Fragment>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant='body1' color='secondary'>
              Use this email address to send tasks to your account
            </Typography>
            <br />
            <Typography variant='body2' color='inherit'>
              You can use this email address for automation or to simply email
              yourself a task. See{' '}
              <Link
                href='https://www.gettingfocused.com/guide/automation.html'
                rel='noopener'
                target='_blank'
              >
                automation
              </Link>{' '}
              for full details of how.
            </Typography>
          </Grid>
          {!isAutomationEnabled && (
            <>
              <Grid item xs={6}>
                <Typography variant='body2' color='inherit'>
                  Sign up for a subscription to unlock this feature.
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Button
                  type='submit'
                  variant='contained'
                  color='primary'
                  onClick={handleSubscriptionOpen}
                  size='small'
                >
                  Get Subscription
                </Button>
              </Grid>
            </>
          )}
          {isAutomationEnabled && (
            <>
              <Grid item xs={12}>
                <Tooltip
                  title={
                    emailIncoming
                      ? 'Copy automation email address'
                      : CREATE_INBOX_MESSAGE
                  }
                >
                  <FormControl className={classes.textField}>
                    <InputLabel htmlFor='standard-adornment-password'>
                      {'Incoming Email Address'}
                    </InputLabel>
                    <Input
                      className={classes.textField}
                      id='emailIncoming'
                      label={'Incoming Email Address (' + emailSuffix + ')'}
                      value={
                        emailIncoming
                          ? emailIncoming + emailSuffix
                          : CREATE_INBOX_MESSAGE
                      }
                      margin='normal'
                      inputProps={{
                        'aria-label': 'email',
                        'data-lpignore': 'true'
                      }}
                      disabled={true}
                      endAdornment={
                        <InputAdornment position='end'>
                          <IconButton
                            aria-label='copy incoming email'
                            disabled={emailIncoming === undefined}
                            onClick={copyEmail}
                          >
                            <FileCopyIcon />
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                  </FormControl>
                </Tooltip>
              </Grid>
              <Grid item xs={12}>
                <Button
                  type='submit'
                  variant='contained'
                  color='primary'
                  onClick={handleWarningOpen}
                  size='small'
                >
                  Create New Inbox
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Fragment>
      <Snackbar
        open={openAlert}
        autoHideDuration={3000}
        onClose={handleAlertClose}
      >
        <Alert onClose={handleAlertClose} severity='success'>
          Email address copied to clipboard
        </Alert>
      </Snackbar>
      <Dialog
        open={openWarning}
        onClose={handleWarningClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>
          {'Create new incoming email address?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            <Typography variant='body1' color='secondary'>
              {emailIncoming
                ? 'You will lose your existing incoming email address and will not be able to recover it.'
                : 'You are about to enable automation.'}
            </Typography>
            <br />
            <Typography variant='body2' color='inherit'>
              Are you sure you want to create a new incoming email address?
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWarningClose} color='primary' autoFocus>
            No
          </Button>
          <Button onClick={createIncEmail} color='primary'>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </SettingsPanel>
  )
})

export default SettingsAccountAutomation
